import {
  ArrowBigLeft,
  Ban,
  Calendar,
  Copy,
  Delete,
  DollarSign,
  Edit,
  Eye,
  Filter,
  Loader2,
  Megaphone,
  MoreHorizontal,
  Plus,
  Search,
  StretchVertical,
  Users,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  useChangeCampaignsStatusApiCallMutation,
  useGenerateCodesMutation,
  useGetCampaignsApiCallQuery,
  useGetCodesApiCallQuery,
  useGetTokenPriceConversionApiCallQuery,
} from "../../services/api/campaigns/campaigns.api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";

import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { ApiError } from "../../services/api/auth/auth.type";
import useDebounce from "../../hooks/useDebounce";
import { Campaign } from "../../services/api/campaigns/campaigns.type";
import { ConnectButton, useConnectModal } from "@rainbow-me/rainbowkit";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useGetProfileApiCallQuery } from "@/services/api/auth/auth.api";
// Mock campaign data
// const mockCampaigns: Campaign[] = [
//   {
//     id: "1",
//     name: "Project X Launch Airdrop",
//     description: "Celebrate our mainnet launch with exclusive token rewards",
//     status: "active",
//     startDate: "2024-01-15T00:00:00Z",
//     endDate: "2024-02-15T23:59:59Z",
//     tokenName: "Project X Token",
//     tokenSymbol: "PXT",
//     contractAddress: "0x1234567890abcdef1234567890abcdef12345678",
//     totalPool: 1000000,
//     participants: 3245,
//     claimModel: "post_campaign",
//     claimStartDate: "2024-02-16T00:00:00Z",
//     claimEndDate: "2024-03-16T23:59:59Z",
//     tasks: [],
//   },
//   {
//     id: "2",
//     name: "Community Growth Campaign",
//     description: "Grow our community and engage with early supporters",
//     status: "active",
//     startDate: "2024-01-01T00:00:00Z",
//     endDate: "2024-01-31T23:59:59Z",
//     tokenName: "Growth Token",
//     tokenSymbol: "GWT",
//     contractAddress: "0xabcdef1234567890abcdef1234567890abcdef12",
//     totalPool: 500000,
//     participants: 1832,
//     claimModel: "instant",
//     tasks: [],
//   },
//   {
//     id: "3",
//     name: "Token Migration Incentive",
//     description: "Incentivize users to migrate to our new token contract",
//     status: "ended",
//     startDate: "2023-12-01T00:00:00Z",
//     endDate: "2023-12-31T23:59:59Z",
//     tokenName: "Migration Token",
//     tokenSymbol: "MGT",
//     contractAddress: "0x9876543210fedcba9876543210fedcba98765432",
//     totalPool: 750000,
//     participants: 5621,
//     claimModel: "post_campaign",
//     claimStartDate: "2024-01-01T00:00:00Z",
//     claimEndDate: "2024-02-01T23:59:59Z",
//     tasks: [],
//   },
//   {
//     id: "4",
//     name: "Beta Tester Rewards",
//     description: "Reward our dedicated beta testers",
//     status: "upcoming",
//     startDate: "2024-03-01T00:00:00Z",
//     endDate: "2024-03-31T23:59:59Z",
//     tokenName: "Beta Token",
//     tokenSymbol: "BTA",
//     contractAddress: "0xfedcba9876543210fedcba9876543210fedcba98",
//     totalPool: 300000,
//     participants: 0,
//     claimModel: "instant",
//     tasks: [],
//   },
//   {
//     id: "5",
//     name: "Partnership Celebration",
//     description: "Celebrate our new strategic partnerships",
//     status: "claimable",
//     startDate: "2023-11-01T00:00:00Z",
//     endDate: "2023-11-30T23:59:59Z",
//     tokenName: "Partnership Token",
//     tokenSymbol: "PTN",
//     contractAddress: "0x1111222233334444555566667777888899990000",
//     totalPool: 400000,
//     participants: 2156,
//     claimModel: "post_campaign",
//     claimStartDate: "2023-12-01T00:00:00Z",
//     claimEndDate: "2024-01-01T23:59:59Z",
//     tasks: [],
//   },
// ];

const CodeManagement: React.FC = () => {
  const { id, userId } = useParams<{ id: string; userId: string }>();
  const [filtercampaigns, setFilterCampaigns] = useState<Campaign[] | []>([]);
  const [isFilter, setIsFilter] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);

  const { openConnectModal } = useConnectModal();
  const { data: profileData } = useGetProfileApiCallQuery();

  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  const debouncedSearch = useDebounce(searchTerm, 500);

  // api call
  const {
    data: campaignsData,
    isLoading: isLoadingCampaigns,
    isError,
  } = useGetCodesApiCallQuery({
    page: page,
    campaignId: id,
    limit: 10,
  });
  const [changeCampaignsStatus] = useChangeCampaignsStatusApiCallMutation();
  // api call ended

  const handleChangeStatusFilter = (status: string) => {
    if (status.toLowerCase() === "ended") {
      setIsFilter(true);
      setFilterCampaigns((prev) => {
        return prev.filter((campaign) => campaign.status === status);
      });
    } else {
      setStatusFilter(status);
    }
  };

  const [openAddCodes, setOpenAddCodes] = useState(false);
  const [codeCount, setCodeCount] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  // Add this mutation (you need to create it in your API slice)
  const [generateCodes] = useGenerateCodesMutation(); // ← Create this!

  const handleGenerateCodes = async () => {
    if (!codeCount || Number(codeCount) <= 0) {
      toast.error("Please enter a valid number of codes");
      return;
    }

    setIsGenerating(true);
    try {
      const result = await generateCodes({
        campaignId: id!,
        count: Number(codeCount),
        userId: userId,
      }).unwrap();

      if (result.responseCode === 200) {
        toast.success(`Successfully generated ${codeCount} codes!`);
        setOpenAddCodes(false);
        setCodeCount("");
        // Optionally refetch codes
        // refetch();
      }
    } catch (error: any) {
      toast.error(error?.data?.responseMessage || "Failed to generate codes");
    } finally {
      setIsGenerating(false);
    }
  };
  const getStatusBadge = (status: Campaign["status"]) => {
    const variants = {
      upcoming: "secondary",
      active: "default",
      completee: "outline",
      claimable: "default",
      archived: "secondary",
    } as const;

    const colors = {
      upcoming: "bg-blue-100 text-blue-800",
      active: "bg-green-100 text-green-800",
      ended: "bg-gray-100 text-gray-800",
      claimable: "bg-purple-100 text-purple-800",
      archived: "bg-yellow-100 text-yellow-800",
    };

    return (
      <Badge className={colors[status as keyof typeof colors]}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const confirmDelete = async () => {
    setIsDeleteLoading(true);
    try {
      const data = await changeCampaignsStatus({
        campaignId: deleteTargetId as string,
        status: "Delete",
      }).unwrap();

      if (data.responseCode === 200) {
        toast.success(data.responseMessage);
        setDeleteTargetId(null);
      }
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = fetchError.data as ApiError;
      toast.error(errorData?.responseMessage || "Error deleting campaign");
    } finally {
      setIsDeleteLoading(false);
      setDeleteTargetId(null);
    }
  };

  const changeStatus = async (status: string, campaignId: string) => {
    try {
      const data = await changeCampaignsStatus({
        campaignId: campaignId,
        status: status,
      }).unwrap();

      if (data.responseCode === 200) {
        toast.success(data.responseMessage);
      } else {
        toast.error(data.responseMessage);
      }
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = fetchError.data as ApiError;
      toast.error(
        errorData?.responseMessage || "Error changing campaign status"
      );
    } finally {
      // setDeleteTargetId(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleDistribution = () => {
    if (openConnectModal) {
      openConnectModal();
    }
  };

  useEffect(() => {
    if (
      !isError &&
      campaignsData &&
      campaignsData.result?.docs &&
      campaignsData?.result?.docs.length > 0
    ) {
      setFilterCampaigns(campaignsData.result?.docs);
    }
  }, [isError]);
  const { data: tokenPriceData } = useGetTokenPriceConversionApiCallQuery();

  const getUSDTBalance = (walletBalance: number) => {
    const usdtPrice =
      tokenPriceData?.result?.data?.find((item: any) => item.coin === "usdt")
        ?.tokenPrice || 0;
    const balance = walletBalance * usdtPrice || 0;
    return balance;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold ">User Campaign Codes</h1>
          <p className="text-sm  mt-1">
            Manage and generate invite codes for campaign: <strong>{id}</strong>
          </p>
        </div>

        <div className="flex items-center gap-3">
          <span
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 cursor-pointer "
          >
            <ArrowBigLeft className="h-5 w-5" />
            Back
          </span>

          {/* Add Codes Button + Dialog */}
          <Dialog open={openAddCodes} onOpenChange={setOpenAddCodes}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Codes
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Generate Invite Codes</DialogTitle>
                <DialogDescription>
                  How many new invite codes would you like to generate for this
                  campaign?
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="code-count" className="text-right">
                    Quantity
                  </Label>
                  <Input
                    id="code-count"
                    type="number"
                    min="1"
                    max="10000"
                    value={codeCount}
                    onChange={(e) => setCodeCount(e.target.value)}
                    className="col-span-3"
                    placeholder="e.g. 500"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setOpenAddCodes(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleGenerateCodes}
                  disabled={isGenerating || !codeCount || Number(codeCount) < 1}
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>Generate Codes</>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters and Search */}
      <Card className="mt-4">
        <CardContent>
          <div className="rounded-md border mt-4">
            <Table>
              <TableHeader className="bg-gray-100 dark:bg-gray-800">
                <TableRow>
                  <TableHead>Codes</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Dates</TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {isLoadingCampaigns ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center h-24">
                      Loading....
                    </TableCell>
                  </TableRow>
                ) : (
                  !isError &&
                  campaignsData?.result?.docs &&
                  campaignsData?.result?.docs?.length > 0 &&
                  campaignsData?.result?.docs?.map((campaign) => {
                    return (
                      <TableRow key={campaign._id}>
                        <TableCell>
                          <div>
                            <div className="font-medium  flex gap-2">
                              {campaign.code}
                              {campaign.code ? (
                                <Copy
                                  className=" h-4 cursor-pointer"
                                  onClick={() => {
                                    navigator.clipboard.writeText(
                                      campaign.code
                                    );
                                    toast.success("Code copied!");
                                  }}
                                />
                              ) : (
                                <span>--</span>
                              )}{" "}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(campaign.status.toLowerCase())}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div>{formatDate(campaign.createdAt)}</div>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>

            {campaignsData?.result && campaignsData?.result?.pages > 1 && (
              <div className="flex justify-end items-center mt-4 gap-2">
                <Button
                  variant="outline"
                  disabled={page === 1}
                  onClick={() => setPage((prev) => prev - 1)}
                >
                  Previous
                </Button>
                <span>
                  Page {page} of {campaignsData.result?.pages}
                </span>
                <Button
                  variant="outline"
                  disabled={page === campaignsData.result.pages}
                  onClick={() => setPage((prev) => prev + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </div>

          {!isLoadingCampaigns &&
            (isError ||
              (campaignsData?.result?.docs &&
                campaignsData.result.docs.length === 0)) && (
              <div className="text-center py-12">
                <Megaphone className="h-12 w-12  mx-auto mb-4" />
                <h3 className="text-lg font-medium  mb-2">No Code found</h3>
              </div>
            )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CodeManagement;
